'use client'

import Link from 'next/link'
import React from 'react'

import { PromotionBadge } from '@/components/Promotions/PromotionBadge'
import { useHydratedNow } from '@/hooks/useHydratedNow'
import type { Locale } from '@/lib/i18n/config'
import { getPromotionCardProps } from '@/lib/promotions/promotionCard'
import type { PromotionListItem } from '@/lib/promotions/promotionsQuery'
import { useMessages } from '@/lib/i18n/useMessages'
import { cn } from '@/utilities/ui'

type RoomPromotionCardProps = {
  className?: string
  idPrefix?: string
  locale: Locale
  promotion: PromotionListItem
}

export function RoomPromotionCard({
  className,
  idPrefix = 'room-promotion',
  locale,
  promotion,
}: RoomPromotionCardProps) {
  const offerNowMs = useHydratedNow()
  const messages = useMessages()
  const gridCopy = messages.promotions.grid
  const roomCopy = messages.roomDetail
  const card = getPromotionCardProps(
    promotion,
    locale,
    {
      imageAltFallback: gridCopy.imageAltFallback,
      offerEndsInDaysHours: gridCopy.offerEndsInDaysHours,
      offerEndsInHoursMinutes: gridCopy.offerEndsInHoursMinutes,
      offerEndsInMonthsDays: gridCopy.offerEndsInMonthsDays,
      offerEndsInYearsMonths: gridCopy.offerEndsInYearsMonths,
      offerEndsSoon: gridCopy.offerEndsSoon,
      permanentOffer: gridCopy.permanentOffer,
      readMore: roomCopy.promotionReadMore,
      readMoreAriaLabel: gridCopy.readMoreAriaLabel,
    },
    { nowMs: offerNowMs, showLocationBadge: false },
  )
  const badge = card.badges[0]
  const headingId = `${idPrefix}-${promotion.id}`

  return (
    <article
      aria-labelledby={headingId}
      className={cn(
        'flex h-full flex-col rounded-[10px] border border-black/15 bg-white/80 p-4 shadow-sm',
        className,
      )}
    >
      <div className="flex flex-1 flex-col">
        {badge ? (
          <div className="mb-2">
            <PromotionBadge label={badge.label} variant={badge.variant} />
          </div>
        ) : null}

        <h3 className="font-outfit text-lg font-medium leading-6 text-dark-800" id={headingId}>
          {card.title}
        </h3>

        {card.body ? (
          <p className="mt-2 font-outfit text-base leading-6 text-dark-800/75">{card.body}</p>
        ) : null}
      </div>

      <Link
        aria-label={card.cta.ariaLabel}
        className="mt-2 inline-flex w-fit font-outfit text-base font-medium text-dark-800 underline decoration-dark-800/40 underline-offset-4 transition-colors hover:text-dark-800/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/60 md:mt-4"
        href={card.cta.href}
      >
        {card.cta.label}
      </Link>
    </article>
  )
}
